import { DEFAULT_PAGE_METADATA, PageMetadata } from '@flakiness/server/common/pageMetadata.js';

export { type PageMetadata };

// The report viewer is a separate app shell (web/src/viewer.html) served on
// its own host, and it carries the same strings in its static <head>.
export const REPORT_VIEWER_PAGE_METADATA: PageMetadata = {
  title: 'Flakiness.io Report Viewer',
  description: 'Test Analytics for GitHub and GitLab',
  imageAlt: 'Flakiness.io Report Viewer',
};

// The server injects <title>, the description, the canonical link and the Open
// Graph tags into the app shell for the initial request (see
// server/src/node/openGraph.ts). Client-side navigations never reload that
// shell, so every page re-declares its own metadata through this function.
// Fields a page leaves out fall back to the site defaults, which keeps the
// previous page's metadata from leaking into the next one.
export function setPageMetadata(metadata: PageMetadata) {
  const resolved = {
    ...DEFAULT_PAGE_METADATA,
    ...(metadata ?? {}),
  };

  const canonicalURL = new URL(window.location.href);
  canonicalURL.search = '';
  canonicalURL.hash = '';
  const imageURL = new URL(resolved.imagePath, window.location.origin).toString();

  document.title = resolved.title;
  setMetaContent('name', 'description', resolved.description);
  setMetaContent('property', 'og:title', resolved.title);
  setMetaContent('property', 'og:description', resolved.description);
  setMetaContent('property', 'og:url', canonicalURL.toString());
  setMetaContent('property', 'og:image', imageURL);
  setMetaContent('property', 'og:image:alt', resolved.imageAlt);
  setMetaContent('name', 'twitter:title', resolved.title);
  setMetaContent('name', 'twitter:description', resolved.description);
  setMetaContent('name', 'twitter:image', imageURL);
  setCanonicalHref(canonicalURL.toString());
}

function setMetaContent(keyAttribute: 'name'|'property', key: string, content: string) {
  let meta = document.head.querySelector(`meta[${keyAttribute}="${key}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(keyAttribute, key);
    document.head.append(meta);
  }
  meta.setAttribute('content', content);
}

function setCanonicalHref(href: string) {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.append(canonical);
  }
  canonical.setAttribute('href', href);
}
